import {
    endOfISOWeek,
    endOfMonth,
    endOfQuarter,
    endOfYear,
    format,
    isSameDay,
    isSameMonth,
    isSameYear,
    parse,
    startOfISOWeek,
    startOfMonth,
    startOfQuarter,
    startOfYear,
} from "date-fns";
import {
    fullMonthFullYearFormat,
    quarterFullYearFormat,
    yearOnlyFormat,
} from "@common/helpers/date";
import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import {
    AccountingReportComparison,
    AccountingReportComparisonDiff,
    AccountingReportCycle,
    AccountingReportCycleOptionType,
    AccountingReportCycleTotal,
    AccountingReportRatioType,
    ClassBasedCycle,
    TimeBasedAccountingReportCycle,
} from "../constants";
import { ReportFilters } from "../types/filters/reports";
import { ReportPeriodWithLabel } from "../types/reports/types";
import {
    accountingNumberFormatter,
    differenceAbsoluteFormatterResult,
    differencePercentageFormatter,
    FormattedAmount,
    formatAmountWithNumberFormatResult,
    formatMoneyColumnHeader,
    formatPercentWithNumberFormatResult,
    ratioPercentageFormatter,
} from "./number";

export const REPORT_DATA_LABEL_FORMAT_WEEKLY = "yyyy-MM-dd";
const WEEK_OUTPUT_FORMAT = "MMM d";
const MONTH_OUTPUT_FORMAT = "MMM";
const PERIOD_LABEL_DAY_FORMAT = "MMM d";
const PERIOD_LABEL_END_SAME_MONTH_FORMAT = "d";
const PERIOD_LABEL_YEAR_SUFFIX_FORMAT = ", yyyy";

export enum ReportFormat {
    PDF = "pdf",
    XLSX = "xlsx",
}

interface ReportLabelOutputParams {
    labelDate: Date;
    filters: ReportFilters;
    fullRange?: boolean;
    printYear?: boolean;
}

function formatReportLabelOutput({
    labelDate,
    filters,
    fullRange = false,
    printYear = false,
}: ReportLabelOutputParams) {
    if (filters.cycle === AccountingReportCycle.YEAR && !fullRange) {
        return format(labelDate, "yyyy");
    }
    const outputStr = format(
        labelDate,
        filters.cycle === AccountingReportCycle.WEEK || fullRange
            ? WEEK_OUTPUT_FORMAT
            : MONTH_OUTPUT_FORMAT,
    );
    if (printYear) {
        return `${outputStr} ${format(labelDate, "yyyy")} `;
    } else {
        return outputStr;
    }
}

export function applyReportFiltersToLabel(
    filters: ReportFilters,
    labelDate: Date,
) {
    if (filters.start && labelDate.getTime() < filters.start.getTime()) {
        labelDate = filters.start;
    }
    if (filters.end && labelDate.getTime() > filters.end.getTime()) {
        labelDate = filters.end;
    }

    return labelDate;
}

export function printLabel(
    text: string,
    filters: ReportFilters,
    fullRange: boolean = false,
) {
    if (!text || filters.cycle === AccountingReportCycleTotal.TOTAL) {
        return text;
    }
    const inputDateFormat = getLabelFormatForCycle(filters.cycle);
    const labelDateStart = applyReportFiltersToLabel(
        filters,
        parse(text, inputDateFormat, new Date()),
    );
    const printYear = labelDateStart.getFullYear() !== new Date().getFullYear();
    let labelOutput = formatReportLabelOutput({
        labelDate: labelDateStart,
        filters,
        fullRange,
        printYear: false,
    });
    if (fullRange) {
        const labelDateEnd = applyReportFiltersToLabel(
            filters,
            getCycleEndForDate(labelDateStart, filters.cycle),
        );
        if (!isSameDay(labelDateEnd, labelDateStart)) {
            labelOutput = `${labelOutput} - ${formatReportLabelOutput({
                labelDate: labelDateEnd,
                filters,
                fullRange,
                printYear,
            })}`;
        }
    }

    return labelOutput;
}

export function getCycleStartForDate(
    date: Date,
    cycle: AccountingReportCycle | "total",
) {
    switch (cycle) {
        case AccountingReportCycle.WEEK:
            return startOfISOWeek(date);
        case AccountingReportCycle.MONTH:
            return startOfMonth(date);
        case AccountingReportCycle.QUARTER:
            return startOfQuarter(date);
        case AccountingReportCycle.YEAR:
            return startOfYear(date);
        default:
            return startOfMonth(date);
    }
}

export function getCycleEndForDate(
    date: Date,
    cycle: AccountingReportCycle | "total",
) {
    switch (cycle) {
        case AccountingReportCycle.WEEK:
            return endOfISOWeek(date);
        case AccountingReportCycle.MONTH:
            return endOfMonth(date);
        case AccountingReportCycle.QUARTER:
            return endOfQuarter(date);
        case AccountingReportCycle.YEAR:
            return endOfYear(date);
        default:
            return endOfMonth(date);
    }
}

export function getLabelFormatForCycle(cycle: AccountingReportCycle | "total") {
    switch (cycle) {
        case AccountingReportCycle.WEEK:
            return REPORT_DATA_LABEL_FORMAT_WEEKLY;
        case AccountingReportCycle.MONTH:
            return fullMonthFullYearFormat;
        case AccountingReportCycle.QUARTER:
            return quarterFullYearFormat;
        case AccountingReportCycle.YEAR:
            return yearOnlyFormat;
        default:
            return fullMonthFullYearFormat;
    }
}

interface FormatWeekLabelParams {
    periodStart: Date;
    periodEndInclusive: Date;
    shouldShowYear: boolean;
}

// Repeat a unit only where it changes: the day alone for a one-day span, the
// month again only when the week crosses months, the year on both ends only
// when it crosses years.
function formatWeekLabel({
    periodStart,
    periodEndInclusive,
    shouldShowYear,
}: FormatWeekLabelParams): string {
    const dayWithYearFormat = `${PERIOD_LABEL_DAY_FORMAT}${PERIOD_LABEL_YEAR_SUFFIX_FORMAT}`;

    if (!isSameYear(periodStart, periodEndInclusive)) {
        return `${format(periodStart, dayWithYearFormat)}–${format(periodEndInclusive, dayWithYearFormat)}`;
    }

    if (isSameDay(periodStart, periodEndInclusive)) {
        return format(
            periodStart,
            shouldShowYear ? dayWithYearFormat : PERIOD_LABEL_DAY_FORMAT,
        );
    }

    const endDayFormat = isSameMonth(periodStart, periodEndInclusive)
        ? PERIOD_LABEL_END_SAME_MONTH_FORMAT
        : PERIOD_LABEL_DAY_FORMAT;
    const endFormat = shouldShowYear
        ? `${endDayFormat}${PERIOD_LABEL_YEAR_SUFFIX_FORMAT}`
        : endDayFormat;

    return `${format(periodStart, PERIOD_LABEL_DAY_FORMAT)}–${format(periodEndInclusive, endFormat)}`;
}

export interface PeriodLabelParams {
    periodStart: Date;
    periodEndInclusive: Date;
}

export type PeriodLabelFormatter = (params: PeriodLabelParams) => string;

interface CreatePeriodLabelFormatterParams {
    cycle: AccountingReportCycle | "total";
    rangeStart: Date;
    rangeEndInclusive: Date;
}

/**
 * Resolves the label shape once per report so every column of that report
 * agrees, then labels each period from its own start and inclusive end.
 * Whether weeks carry a year follows the report's own range, not the periods:
 * a single-year report with a comparison spans two calendar years but its
 * `(PY)` columns must stay year-free.
 */
export function createPeriodLabelFormatter({
    cycle,
    rangeStart,
    rangeEndInclusive,
}: CreatePeriodLabelFormatterParams): PeriodLabelFormatter {
    if (cycle !== AccountingReportCycle.WEEK) {
        const labelFormat = getLabelFormatForCycle(cycle);
        return ({ periodStart }) => format(periodStart, labelFormat);
    }

    const shouldShowYear = !isSameYear(rangeStart, rangeEndInclusive);

    return ({ periodStart, periodEndInclusive }) =>
        formatWeekLabel({ periodStart, periodEndInclusive, shouldShowYear });
}

/** Blank rather than `getReportDateRangeLabel`'s "All time", which would name a period the report never filtered. */
export function getOptionalReportDateRangeLabel(
    start?: Date,
    end?: Date,
): string {
    return start && end ? getReportDateRangeLabel(start, end) : "";
}

export function getReportDateRangeLabel(start?: Date, end?: Date): string {
    if (!start && end) {
        return `Before ${format(end, "MMMM do yyyy")}`;
    }

    if (start && !end) {
        return `After ${format(start, "MMMM do yyyy")}`;
    }

    if (!start || !end) {
        return "All time";
    }

    if (
        start.getDate() !== startOfMonth(start).getDate() ||
        end.getDate() !== endOfMonth(end).getDate()
    ) {
        return `${format(start, "MMMM do")} ${
            !isSameYear(start, end) ? start.getFullYear() : ""
        } - ${format(end, "MMMM do")} ${end.getFullYear()}`;
    }

    if (isSameMonth(start, end)) {
        return format(start, "MMMM yyyy");
    }

    return `${format(start, "MMMM")} ${
        !isSameYear(start, end) ? start.getFullYear() : ""
    } - ${format(end, "MMMM")} ${end.getFullYear()}`;
}

const COMPARISON_ABBREVIATIONS: Record<AccountingReportComparison, string> = {
    [AccountingReportComparison.PREVIOUS_YEAR]: "(PY)",
    [AccountingReportComparison.PREVIOUS_PERIOD]: "(PP)",
};

export function getComparisonSuffix(
    comparison: AccountingReportComparison,
    includeSpace = true,
): string {
    const suffix = COMPARISON_ABBREVIATIONS[comparison];
    return includeSpace ? ` ${suffix}` : suffix;
}

export function getComparisonTotalLabel(
    comparison: AccountingReportComparison,
): string {
    return `Total ${getComparisonSuffix(comparison, false)}`;
}

export function assertEqualPeriodCounts(
    current: { periods: unknown[] },
    comparison: { periods: unknown[] },
): void {
    if (current.periods.length !== comparison.periods.length) {
        throw new Error(
            `Trial balance period count mismatch: current has ${current.periods.length} periods, comparison has ${comparison.periods.length} periods`,
        );
    }
}

export function isClassBasedCycle(
    cycle?: AccountingReportCycleOptionType,
): cycle is ClassBasedCycle {
    return (
        cycle === AccountingReportCycle.CLASS ||
        cycle === AccountingReportCycle.SUBCLASS
    );
}

/**
 * Stored cycle values stay untouched, so persisted `SUBCLASS` configs are
 * normalized on read. Generic-preserving so backend and frontend callers keep
 * their own cycle union.
 */
interface ResolveEffectiveReportCycleArgs<
    Cycle extends AccountingReportCycleOptionType | undefined,
> {
    cycle: Cycle;
}

export function resolveEffectiveReportCycle<
    Cycle extends AccountingReportCycleOptionType | undefined,
>({
    cycle,
}: ResolveEffectiveReportCycleArgs<Cycle>):
    | Cycle
    | AccountingReportCycle.CLASS {
    if (cycle === AccountingReportCycle.SUBCLASS) {
        return AccountingReportCycle.CLASS;
    }
    return cycle;
}

export function isTimeBasedCycle(
    cycle?: AccountingReportCycleOptionType,
): cycle is TimeBasedAccountingReportCycle {
    return (
        cycle === AccountingReportCycle.WEEK ||
        cycle === AccountingReportCycle.MONTH ||
        cycle === AccountingReportCycle.QUARTER ||
        cycle === AccountingReportCycle.YEAR
    );
}

export function getTimeBasedCycle(
    cycle?: AccountingReportCycleOptionType,
): TimeBasedAccountingReportCycle | undefined {
    return isTimeBasedCycle(cycle) ? cycle : undefined;
}

export function isGroupSubtotalPeriod(period: ReportPeriodWithLabel): boolean {
    return (period.classIds?.length ?? 0) > 1;
}

export function isGroupEndPeriod(
    periods: ReportPeriodWithLabel[],
    index: number,
): boolean {
    const period = periods[index];
    if (!isGroupSubtotalPeriod(period)) {
        return false;
    }

    for (let i = index + 1; i < periods.length; i++) {
        if (periods[i].isTotal) {
            continue;
        }
        const nextPeriod = periods[i];
        return (
            !isGroupSubtotalPeriod(nextPeriod) ||
            nextPeriod.classIds?.[0] !== period.classIds?.[0]
        );
    }

    return true;
}

// Only N-level "By Class" reports set classColumnRole on their periods.
export function isNLevelClassReport(periods: ReportPeriodWithLabel[]): boolean {
    return periods.some((period) => period.classColumnRole !== undefined);
}

export function getPeriodKey(period: ReportPeriodWithLabel): string {
    let groupSuffix = "";
    if (period.classIds?.[0]) {
        groupSuffix += `-class:${period.classIds[0]}`;
    }
    if (period.entityId) {
        groupSuffix += `-entity:${period.entityId}`;
    }
    if (period.isTotal) {
        groupSuffix += "-total";
    }
    if (period.ratioType) {
        groupSuffix += `-ratio:${period.ratioType}`;
    }
    // A parent's direct and total columns share a class id and sub-period labels,
    // so the role is what disambiguates their keys.
    if (period.classColumnRole) {
        groupSuffix += `-role:${period.classColumnRole}`;
    }
    return `${period.startDate}-${period.endDate}-${period.label}${groupSuffix}`;
}

export function hasMixedReportLedgerBases(
    periods: ReportPeriodWithLabel[],
): boolean {
    const ledgerBases = periods
        .map((period) => period.ledgerBasis)
        .filter((basis) => basis !== undefined);
    return new Set(ledgerBases).size > 1;
}

const DIFFERENCE_SYMBOLS: Record<AccountingReportComparisonDiff, string> = {
    [AccountingReportComparisonDiff.ABSOLUTE]: "$",
    [AccountingReportComparisonDiff.PERCENTAGE]: "%",
};

export function getDifferencePeriodLabel(
    comparisonDiff: AccountingReportComparisonDiff,
    comparisonType?: AccountingReportComparison,
): string {
    const symbol = DIFFERENCE_SYMBOLS[comparisonDiff];
    const suffix = comparisonType
        ? ` ${getComparisonSuffix(comparisonType, false)}`
        : "";
    return `${symbol} Change${suffix}`;
}

export function formatMoneyPeriodHeader(
    period: ReportPeriodWithLabel,
    numberFormat?: NumberFormatValues,
): string {
    // Ratio and difference labels already embed their own `%` or `$`.
    const hasOwnSymbol =
        period.ratioType !== undefined || period.comparisonDiff !== undefined;
    if (!numberFormat || hasOwnSymbol) {
        return period.label;
    }
    return formatMoneyColumnHeader(period.label, numberFormat);
}

export interface FormatReportValueOptions {
    comparisonDiff?: AccountingReportComparisonDiff;
    ratioType?: AccountingReportRatioType;
    /** Resolved report number format; omit to keep today's hardcoded output. */
    numberFormat?: NumberFormatValues;
}

export function formatReportValueResult(
    value: number,
    { comparisonDiff, ratioType, numberFormat }: FormatReportValueOptions = {},
): FormattedAmount {
    if (ratioType) {
        if (numberFormat) {
            return formatPercentWithNumberFormatResult({ numberFormat, value });
        }
        const text = ratioPercentageFormatter(value);
        // isNegative follows the displayed sign, not the raw value.
        return { isNegative: value !== 0 && text.startsWith("-"), text };
    }

    if (comparisonDiff) {
        return formatDifferenceValueResult({
            value,
            comparisonDiff,
            numberFormat,
        });
    }

    if (numberFormat) {
        return formatAmountWithNumberFormatResult({ numberFormat, value });
    }

    return {
        isNegative: value < 0,
        text: accountingNumberFormatter(value),
    };
}

interface FormatDifferenceValueParams {
    value: number;
    comparisonDiff: AccountingReportComparisonDiff;
    numberFormat?: NumberFormatValues;
}

function formatDifferenceValueResult({
    value,
    comparisonDiff,
    numberFormat,
}: FormatDifferenceValueParams): FormattedAmount {
    switch (comparisonDiff) {
        case AccountingReportComparisonDiff.PERCENTAGE: {
            if (numberFormat) {
                return formatPercentWithNumberFormatResult({
                    numberFormat,
                    value,
                });
            }
            const text = differencePercentageFormatter(value);
            return { isNegative: value !== 0 && text.startsWith("-"), text };
        }
        case AccountingReportComparisonDiff.ABSOLUTE:
            return differenceAbsoluteFormatterResult(value, undefined, {
                numberFormat,
            });
    }
}

export function formatReportValue(
    value: number,
    options: FormatReportValueOptions = {},
): string {
    return formatReportValueResult(value, options).text;
}

export function calculateDifference(
    currentValue: number,
    comparisonValue: number,
    type: AccountingReportComparisonDiff,
): number | null {
    switch (type) {
        case AccountingReportComparisonDiff.ABSOLUTE:
            return currentValue - comparisonValue;
        case AccountingReportComparisonDiff.PERCENTAGE:
            if (comparisonValue === 0) {
                return null;
            }

            return (
                ((currentValue - comparisonValue) / Math.abs(comparisonValue)) *
                100
            );
    }
}

export function addDifferenceValuesToArrayMultiTotal(
    values: number[],
    comparisons: AccountingReportComparison[],
    comparisonDiffs: AccountingReportComparisonDiff[],
): number[] {
    const result: number[] = [values[0] ?? 0];
    for (let c = 0; c < comparisons.length; c++) {
        const compValue = values[1 + c] ?? 0;
        result.push(compValue);
        for (const diff of comparisonDiffs) {
            const diffValue = calculateDifference(
                values[0] ?? 0,
                compValue,
                diff,
            );
            result.push(diffValue ?? 0);
        }
    }
    return result;
}

export function addDifferenceValuesToArrayMultiCycle(
    values: number[],
    comparisons: AccountingReportComparison[],
    comparisonDiffs: AccountingReportComparisonDiff[],
): number[] {
    const groupSize = 1 + comparisons.length;
    const result: number[] = [];
    for (let i = 0; i < values.length; i += groupSize) {
        const current = values[i] ?? 0;
        result.push(current);
        for (let c = 0; c < comparisons.length; c++) {
            const compValue = values[i + 1 + c] ?? 0;
            result.push(compValue);
            for (const diff of comparisonDiffs) {
                const diffValue = calculateDifference(current, compValue, diff);
                result.push(diffValue ?? 0);
            }
        }
    }
    return result;
}

export function getReportEntityTitle(
    entityName: string,
    cycle?: AccountingReportCycleOptionType,
): string {
    if (cycle === AccountingReportCycle.ENTITY) {
        return "All Entities";
    }
    return entityName;
}

export function getReportTypeTitle(
    reportType: string,
    cycle?: AccountingReportCycleOptionType,
): string {
    if (cycle === AccountingReportCycle.CLASS) {
        return `${reportType} by Class`;
    }
    if (cycle === AccountingReportCycle.SUBCLASS) {
        return `${reportType} by Subclass`;
    }
    return reportType;
}

export function getRatioPeriodLabel(
    ratioType: AccountingReportRatioType,
): string {
    switch (ratioType) {
        case AccountingReportRatioType.INCOME_PERCENTAGE:
            return "% of income";
        case AccountingReportRatioType.EXPENSE_PERCENTAGE:
            return "% of expense";
    }
}
